import {
	AppBar,
	Badge,
	Box,
	Button,
	CircularProgress,
	createTheme,
	Dialog,
	DialogContent,
	IconButton,
	Paper,
	Slide,
	Toolbar,
	Typography,
	useMediaQuery,
} from "@mui/material"
import { forwardRef, useContext, useEffect, useRef, useState } from "react"
import { AdminContext } from "../views/Admin"
import CloseIcon from "@mui/icons-material/Close"
import {
	extUpdatePostV2,
	getFileUrl,
	getPostDataForEditV2,
	uploadMedia,
	uploadMediaWithProgress,
} from "../util/firebase"
import { blobToDataURL, handleVideoFile, resizeFile } from "../util/media"

const Transition = forwardRef(function Transition(props, ref) {
	return <Slide direction="down" ref={ref} {...props} />
})

const mdTheme = createTheme({
	palette: {
		primary: {
			main: "#132242",
		},
		secondary: {
			main: "#DF0A0A",
		},
		white: {
			main: "white",
		},
	},
})

const EditGallery = () => {
	const adminContext = useContext(AdminContext)
	const editGalleryOpen = adminContext.editGalleryOpen
	const setEditGalleryOpen = adminContext.setEditGalleryOpen
	const handleOpenSnackbar = adminContext.handleOpenSnackbar
	const refreshPost = adminContext.refreshPost

	const post = adminContext.post

	const matches = useMediaQuery(mdTheme.breakpoints.up("md"))

	const [postStatus, setPostStatus] = useState("")

	//const [media, setMedia] = useState([])
	const [exteriorMedia, setExteriorMedia] = useState([])
	const [interiorMedia, setInteriorMedia] = useState([])
	const [videoMedia, setVideoMedia] = useState([])

	const [videoProgress, setVideoProgress] = useState([])

	const [loading, setLoading] = useState(false)
	/* const [noFilters, setNoFilters] = useState(true) */

	const fileInputExteriorRef = useRef(null)
	const fileInputInteriorRef = useRef(null)
	const fileInputVideoRef = useRef(null)

	const handleSaveGallery = async () => {
		const media = [...exteriorMedia, ...interiorMedia, ...videoMedia]
		try {
			console.log(media)
			const response = await extUpdatePostV2({ id: post.id, media: media })
			if (response?.data?.status === 0) {
				await refreshPost(post.id, postStatus)
				handleOpenSnackbar("תמונות עודכנו בהצלחה", "success")
			}
		} catch (e) {
			console.log(e)
			handleOpenSnackbar("תקלה בעידכון תמונות", "error")
		}
		setEditGalleryOpen(false)
	}

	useEffect(() => {
		const _getPostDataForEdit = async () => {
			try {
				setLoading(true)
				const response = await getPostDataForEditV2({ id: post.id })
				setLoading(false)
				if (response.data.status === 0) {
					setPostStatus(response.data.data.status)
					if (response.data.data.media) {
						let _mediaArray = response.data.data.media.filter((e) => e.type === "EXTERIOR")

						for (let i = 0; i < _mediaArray.length; i++) {
							try {
								const downloadUrl = await getFileUrl(_mediaArray[i].fileName)
								// eslint-disable-next-line no-loop-func
								setExteriorMedia((prevState) => [...prevState, { ..._mediaArray[i], url: downloadUrl }])
							} catch (error) {
								console.log(error)
							}
						}
						_mediaArray = response.data.data.media.filter((e) => e.type === "INTERIOR")

						for (let i = 0; i < _mediaArray.length; i++) {
							try {
								const downloadUrl = await getFileUrl(_mediaArray[i].fileName)
								// eslint-disable-next-line no-loop-func
								setInteriorMedia((prevState) => [...prevState, { ..._mediaArray[i], url: downloadUrl }])
							} catch (error) {
								console.log(error)
							}
						}
						_mediaArray = response.data.data.media.filter((e) => e.type === "VIDEO")
						_mediaArray.forEach((e) => {
							setVideoMedia((prevState) => [...prevState, { ...e, url: "/videoPlaceholder.jpg" }])
						})
					}
				}
			} catch (e) {
				console.log(e.message)
				setLoading(false)
			}
		}

		if (post?.id && editGalleryOpen) {
			_getPostDataForEdit()
		} else {
			setInteriorMedia([])
			setExteriorMedia([])
			setVideoMedia([])
		}

		return () => {
			setInteriorMedia([])
			setExteriorMedia([])
			setVideoMedia([])
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editGalleryOpen])

	const uploadFile = async (file) => {
		console.log(`About to upload ${file.fullPath}`)
		if (file.object) {
			await uploadMedia(file.fullPath, file.object, post.uid, post.id, file.contentType)
		} else {
			return
		}
	}

	const uploadFileWithProgress = async (file, index) => {
		console.log(`About to upload ${file.fullPath} with Progress`)
		if (file.object) {
			await uploadMediaWithProgress(
				file.fullPath,
				file.object,
				post.uid,
				post.id,
				file.contentType,
				(progress) => {
					console.log(`Progress for index = ${index} is ${progress}`)
					setVideoProgress((prevState) => {
						const newState = prevState.map((e, i) => {
							if (i === index) {
								return progress.toString()
							} else {
								return e
							}
						})
						return newState
					})
				},
				(url) => {
					console.log(`Index ${index} is Complete...`)
					setVideoProgress((prevState) => {
						const newState = prevState.filter((e, i) => i !== index)
						return newState
					})
				}
			)
		} else {
			return
		}
	}

	const handleFileInputChange = async (e, mediaType, setMediaState) => {
		const files = e.target.files
		let _media = []
		const uploadPath = "public/media/"
		for (let i = 0; i < files.length; i++) {
			const file = files[i]
			const fileType = file.type.substring(0, file.type.indexOf("/"))
			let fullPath = null
			switch (fileType) {
				case "image":
					const imageBlob = await resizeFile(file)
					const imageFile = await blobToDataURL(imageBlob)
					fullPath = uploadPath + post.id + "/images/" + performance.now().toString() + ".jpg"
					_media.push({
						file: imageFile,
						contentType: file.type,
						fullPath: fullPath,
						object: imageBlob,
						mediaType: mediaType,
					})
					break
				case "video":
					const processedFile = await handleVideoFile(file)
					fullPath = uploadPath + post.id + "/videoStaging/" + performance.now().toString()
					_media.push({
						file: processedFile,
						contentType: file.type,
						fullPath: fullPath,
						object: processedFile,
						mediaType: mediaType,
					})
					break
				default:
					break
			}
		}
		if (_media?.length > 0) {
			const uploadPromises = _media.map((file, i) => {
				const fileType = file.contentType.substring(0, file.contentType.indexOf("/"))
				if (fileType === "video") {
					setVideoProgress((prevState) => [...prevState, "Loading..."])
					return uploadFileWithProgress(file, i)
				} else {
					return uploadFile(file)
				}
			})

			await Promise.all(uploadPromises)
			const _newMedia = []
			const staticPromise = Promise.resolve("/videoPlaceholder.jpg")
			const urlPromises = _media.map((file) => {
				if (file.contentType.indexOf("video") > -1) {
					return staticPromise.then((url) => {
						_newMedia.push({
							contentType: file.contentType,
							fileName: file.fullPath.replace("videoStaging", "video") + "/manifest.m3u8",
							type: file.mediaType,
							url: url,
						})
					})
				} else {
					return getFileUrl(file.fullPath)
						.then((url) =>
							_newMedia.push({
								contentType: file.contentType,
								fileName: file.fullPath,
								type: file.mediaType,
								url: url,
							})
						)
						.catch((e) => {
							console.log(e)
						})
				}
			})
			await Promise.all(urlPromises)
			setMediaState((prevState) => [...prevState, ..._newMedia])
		}
	}

	useEffect(() => {
		console.log(exteriorMedia)
	}, [exteriorMedia])

	return (
		<Dialog
			fullScreen={!matches}
			open={editGalleryOpen}
			onClose={() => setEditGalleryOpen(false)}
			TransitionComponent={Transition}
			PaperProps={{
				style: {
					backgroundColor: "white",
					maxHeight: "715px",
				},
			}}
			maxWidth={"md"}
			/* scroll="body" */
			/* sx={{ maxHeight: "715px"}} */
		>
			<AppBar sx={{ position: "relative" }}>
				<Toolbar>
					<IconButton edge="start" color="inherit" onClick={() => setEditGalleryOpen(false)} aria-label="close">
						<CloseIcon />
					</IconButton>
					<Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
						עריכת גלריה
					</Typography>
				</Toolbar>
			</AppBar>
			<DialogContent dividers>
				<Paper sx={{ p: 2 }}>
					<Typography variant="h6" sx={{ display: "block" }}>
						תמונות חוץ
					</Typography>
					<button
						onClick={() => {
							fileInputExteriorRef.current.click()
						}}
					>
						העלאה
					</button>
					<input
						type="file"
						style={{ display: "none" }}
						multiple
						onChange={(e) => handleFileInputChange(e, "EXTERIOR", setExteriorMedia)}
						ref={fileInputExteriorRef}
						accept={"image/*"}
					/>
					<div
						style={{
							padding: 2,
							display: "flex",
							flexDirection: matches ? "row" : "column",
							justifyContent: loading ? "center" : "flex-start",
							flexWrap: "wrap",
							columnGap: "15px",
							rowGap: "15px",
							marginBottom: "20px",
						}}
					>
						{loading && <CircularProgress color="primary" />}
						{!loading && (
							<>
								{exteriorMedia.map((img, index) => (
									<div style={{ position: "relative" }} key={img.url}>
										<Badge
											badgeContent={"X"}
											color="primary"
											style={{ cursor: "pointer" }}
											onClick={() => {
												setExteriorMedia((prevState) => prevState.filter((e, i) => i !== index))
											}}
										>
											<img
												src={img.url}
												style={{ width: "140px", objectFit: "contain", cursor: "pointer" }}
												alt={img.url}
												onClick={() => window.open(img.url)}
											/>
										</Badge>
										<button
											style={{
												border: "0",
												backgroundColor: "rgba(0,0,0,0.5)",
												color: "white",
												position: "absolute",
												bottom: "8px",
												right: "10px",
												cursor: "pointer",
											}}
											onClick={() => {
												setExteriorMedia((prevState) => [prevState[index], ...prevState.filter((e, i) => i !== index)])
											}}
										>
											הפוך לשער
										</button>
									</div>
								))}
							</>
						)}
					</div>
				</Paper>
				<Paper sx={{ p: 2 }}>
					<Typography variant="h6" sx={{ display: "block" }}>
						תמונות פנים
					</Typography>
					<button
						onClick={() => {
							fileInputInteriorRef.current.click()
						}}
					>
						העלאה
					</button>
					<input
						type="file"
						style={{ display: "none" }}
						multiple
						onChange={(e) => handleFileInputChange(e, "INTERIOR", setInteriorMedia)}
						ref={fileInputInteriorRef}
						accept={"image/*"}
					/>
					<div
						style={{
							padding: 2,
							display: "flex",
							flexDirection: matches ? "row" : "column",
							justifyContent: loading ? "center" : "flex-start",
							flexWrap: "wrap",
							columnGap: "15px",
							rowGap: "15px",
							marginBottom: "20px",
						}}
					>
						{loading && <CircularProgress color="primary" />}
						{!loading && (
							<>
								{interiorMedia.map((img, index) => (
									<div style={{ position: "relative" }} key={img.url}>
										<Badge
											badgeContent={"X"}
											color="primary"
											style={{ cursor: "pointer" }}
											onClick={() => {
												setInteriorMedia((prevState) => prevState.filter((e, i) => i !== index))
											}}
										>
											<img
												src={img.url}
												style={{ width: "140px", objectFit: "contain", cursor: "pointer" }}
												alt={img.url}
												onClick={() => window.open(img.url)}
											/>
										</Badge>
									</div>
								))}
							</>
						)}
					</div>
				</Paper>
				<Paper sx={{ p: 2 }}>
					<Typography variant="h6" sx={{ display: "block" }}>
						וידאו
					</Typography>
					<button
						onClick={() => {
							fileInputVideoRef.current.click()
						}}
					>
						העלאה
					</button>
					<input
						type="file"
						style={{ display: "none" }}
						multiple
						onChange={(e) => handleFileInputChange(e, "VIDEO", setVideoMedia)}
						ref={fileInputVideoRef}
						accept={"video/*"}
					/>
					<div
						style={{
							padding: 2,
							display: "flex",
							flexDirection: matches ? "row" : "column",
							justifyContent: loading ? "center" : "flex-start",
							flexWrap: "wrap",
							columnGap: "15px",
							rowGap: "15px",
							marginBottom: "20px",
						}}
					>
						{loading && <CircularProgress color="primary" />}
						{!loading && (
							<>
								{videoMedia.map((img, index) => {
									if (videoProgress.length > 0 && index === videoProgress.length) {
										return null
									} else {
										return (
											<div style={{ position: "relative" }} key={`${index}${img.url}`}>
												<Badge
													badgeContent={"X"}
													color="primary"
													style={{ cursor: "pointer" }}
													onClick={() => {
														setVideoMedia((prevState) => prevState.filter((e, i) => i !== index))
													}}
												>
													<img
														src={img.url}
														style={{ width: "140px", objectFit: "contain", cursor: "pointer" }}
														alt={img.url}
														onClick={() => window.open(img.url)}
													/>
												</Badge>
											</div>
										)
									}
								})}
								{videoProgress.map((progress) => (
									<Box sx={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
										<CircularProgress variant="determinate" color="primary" value={progress} />
										<Box
											sx={{
												top: 0,
												left: 0,
												bottom: 0,
												right: 0,
												position: "absolute",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											<Typography variant="caption" component="div" color="text.secondary">
												{`${Math.round(progress)}%`}
											</Typography>
										</Box>
									</Box>
								))}
							</>
						)}
					</div>
				</Paper>{" "}
				<div style={{ display: "flex", width: "100%", justifyContent: "center", marginBottom: "20px" }}>
					<Button
						sx={{
							m: 2,
							width: matches ? "120px" : "auto",
							backgroundColor: mdTheme.palette.secondary.main,
							"&:hover": { backgroundColor: mdTheme.palette.secondary.main },
						}}
						variant="contained"
						onClick={handleSaveGallery}
					>
						שמירה
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default EditGallery
